import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { Suspense, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";
import { AlertIcon } from "@whoosh/design-system/src/components/icons_v2/general";

import { getMembershipTypeIdFromRoundIdAtom } from "../../../../../../../atoms/selectors/getMembershipTypeIdFromRoundIdAtom/getMembershipTypeIdFromRoundIdAtom";
import { getRateFromRoundIdAtom } from "../../../../../../../atoms/selectors/getRateFromRoundIdAtom/getRatefromRoundIdAtom";
import { RoundInfoBannersQuery } from "./__generated__/RoundInfoBannersQuery.graphql";

type Props = {
  roundId: string;
};
export const RoundInfoBanners = ({ roundId }: Props) => {
  const rate = useAtomValue(getRateFromRoundIdAtom(roundId));
  const rateId = rate?.id;
  const membershipTypeId = useAtomValue(
    getMembershipTypeIdFromRoundIdAtom(roundId)
  );
  const [queryRef, loadQuery, disposeQuery] =
    useQueryLoader<RoundInfoBannersQuery>(RoundInfoBannersQueryString);

  useEffect(() => {
    if (rateId && membershipTypeId) {
      loadQuery(
        { rateId, membershipTypeId },
        { fetchPolicy: "store-and-network" }
      );
    } else {
      disposeQuery();
    }
  }, [disposeQuery, loadQuery, rateId, membershipTypeId]);

  return (
    <Suspense>
      {rateId && membershipTypeId && queryRef ? (
        <RoundInfoBannerQueryRenderer queryRef={queryRef} />
      ) : null}
    </Suspense>
  );
};

const RoundInfoBannerQueryRenderer = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<RoundInfoBannersQuery>;
}) => {
  const data = usePreloadedQuery<RoundInfoBannersQuery>(
    RoundInfoBannersQueryString,
    queryRef
  );
  const rate = data.rateNode;
  const needsVerification = !rate?.appliesToMembershipType;
  return needsVerification ? (
    <InfoBanner
      leadingIcon={AlertIcon}
      colorTheme="WARNING"
      message={t`Rate verification required upon arrival`}
    />
  ) : null;
};

const RoundInfoBannersQueryString = graphql`
  query RoundInfoBannersQuery($rateId: ID!, $membershipTypeId: ID!) {
    rateNode: node(id: $rateId) {
      ... on Rate {
        appliesToMembershipType(membershipTypeId: $membershipTypeId)
      }
    }
  }
`;
